// ACCEPT AN INVITATION TO THE BACK OFFICE (/accept-invitation?invitationId=…): where a staff
// invitation's email link opens (tkt-backend utils/auth inviteLinkOriginFor: a platform invitation
// opens the staff console that sent it; one for a single organizer opens that organizer's dashboard).
// Rendered by the root shell OUTSIDE its sign-in gate and shell: the person is not staff until they
// accept. Sign in as the invited address (a new address gets an account by its email code), see what
// the invitation grants, accept or decline; once accepted, the back office opens.
//
// An invitation to one organizer belongs on the organizer dashboard; one that reaches this page
// anyway is still accepted here, and says where its work is.
import React, { useCallback, useEffect, useState } from 'react';
import { Check, Loader2, LogOut, MailWarning } from 'lucide-react';
import { authClient } from '../lib/authClient';
import { loadSession, signOut } from '../lib/backOfficeApi';
import { derivedHostUrl } from '../lib/backendUrl';
import { Button } from '../ui/Button';
import { SignInPage } from './SignInPage';

const ROLE_LABEL = { owner: 'Owner', admin: 'Admin', member: 'Member', cashier: 'Cashier', finance: 'Finance', support: 'Support', eventManager: 'Event manager', eventAgent: 'Event agent', compliance: 'Compliance' };
const roleLabel = (role) => String(role || '').split(',').map((r) => r.trim()).filter(Boolean).map((r) => ROLE_LABEL[r] || r).join(', ');

export const AcceptInvitationPage = () => {
  const invitationId = new URLSearchParams(window.location.search).get('invitationId') || '';
  const [session, setSession] = useState({ status: 'checking' });
  const refresh = useCallback(async () => setSession(await loadSession()), []);
  useEffect(() => { refresh(); }, [refresh]);

  if (!invitationId) return <Frame><Problem title="This link is incomplete" body="It has no invitation in it. Open the link from your invitation email again, or ask whoever invited you to send a new one." /></Frame>;
  if (session.status === 'checking') {
    return <div className="min-h-screen w-full flex items-center justify-center bg-[#FBFAF7] text-[#6B7280]"><div className="flex items-center gap-2 text-[13px]"><Loader2 className="w-4 h-4 animate-spin" />Checking session…</div></div>;
  }
  if (session.status === 'unknown') {
    return <Frame><Problem title="The server did not answer" body="Your invitation is untouched. Try again in a moment." action={<Button onClick={refresh}>Try again</Button>} /></Frame>;
  }
  if (session.status === 'unauthed') {
    return <SignInPage onSignedIn={refresh} title="Accept your invitation" intro="Sign in with the email address your invitation was sent to." />;
  }
  return <Frame><Invitation id={invitationId} user={session.user} onSignedOut={refresh} /></Frame>;
};

const Invitation = ({ id, user, onSignedOut }) => {
  const [state, setState] = useState({ status: 'loading' });
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    authClient.organization.getInvitation({ query: { id } }).then(({ data, error: e }) => {
      if (!live) return;
      if (e || !data) setState({ status: 'unavailable', message: e?.message || '' });
      else setState({ status: 'ready', invitation: data });
    }).catch(() => { if (live) setState({ status: 'unavailable', message: '' }); });
    return () => { live = false; };
  }, [id]);

  const switchAccount = async () => { await signOut(); onSignedOut(); };

  if (state.status === 'loading') return <div className="flex items-center gap-2 text-[13px] text-[#6B7280]"><Loader2 className="w-4 h-4 animate-spin" />Opening your invitation…</div>;
  if (state.status === 'unavailable') {
    // better-auth answers alike for an invitation that is not this account's, one already used, and
    // one past its expiry: name the account, and the ways out.
    return (
      <Problem
        title="This invitation can’t be opened"
        body={`You’re signed in as ${user?.email || 'another account'}. The invitation may be for a different email address, already used, or expired.${state.message ? ` (${state.message})` : ''}`}
        action={<Button icon={LogOut} onClick={switchAccount}>Sign in with another email</Button>}
      />
    );
  }

  const inv = state.invitation;
  const platform = inv.platformScope === 'all';
  if (state.status === 'declined') return <Problem title="Invitation declined" body="Nothing was added to your account. You can close this page." />;
  if (state.status === 'accepted') {
    const dashboard = dashboardUrl();
    return (
      <div className="space-y-3">
        <span className="w-9 h-9 rounded-full bg-[#E7F4EC] text-[#1F6A3A] inline-flex items-center justify-center"><Check className="w-4 h-4" /></span>
        <h1 className="text-[16px] font-semibold text-[#1F2937]">You’ve joined {inv.organizationName}</h1>
        <p className="text-[12.5px] text-[#6B7280]">Its events, sales and payouts are managed in the organizer dashboard.</p>
        {dashboard && <Button variant="primary" className="w-full" onClick={() => window.location.assign(dashboard)}>Open the organizer dashboard</Button>}
      </div>
    );
  }
  if (inv.status && inv.status !== 'pending') {
    return <Problem title="This invitation was already used" body={`It is ${inv.status}. Ask whoever invited you to send a new one if you still need access.`} />;
  }

  const accept = async () => {
    setBusy('accept'); setError('');
    const { error: e } = await authClient.organization.acceptInvitation({ invitationId: id });
    setBusy('');
    if (e) { setError(e.message || 'The invitation could not be accepted.'); return; }
    if (platform) { window.location.assign('/'); return; }
    setState({ status: 'accepted', invitation: inv });
  };
  const decline = async () => {
    setBusy('decline'); setError('');
    const { error: e } = await authClient.organization.rejectInvitation({ invitationId: id });
    setBusy('');
    if (e) { setError(e.message || 'The invitation could not be declined.'); return; }
    setState({ status: 'declined', invitation: inv });
  };

  const rows = [
    ['Invited by', inv.inviterEmail],
    ['Role', inv.role ? roleLabel(inv.role) : null],
    ['Access', platform ? 'Every organizer on the platform' : inv.organizationName],
    ['Expires', inv.expiresAt ? new Date(inv.expiresAt).toLocaleString('en-GB', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : null],
  ].filter(([, v]) => v);
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-[16px] font-semibold text-[#1F2937]">{platform ? 'Join the Back Office' : `Join ${inv.organizationName}`}</h1>
        <p className="text-[12.5px] text-[#6B7280] mt-1">
          {platform ? 'You’ve been invited to the team that runs the platform: organizers, events, orders, payouts and gates.' : 'You’ve been invited to an organizer on the platform.'}
        </p>
      </div>
      <dl className="rounded-md border border-[#EEEDEA] bg-[#FBFAF7] px-3 py-2.5 space-y-1.5 text-[12px]">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-baseline gap-2">
            <dt className="text-[#9CA3AF] w-[90px] shrink-0">{k}</dt>
            <dd className="text-[#1F2937] font-medium min-w-0 break-words">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="text-[11.5px] text-[#9CA3AF]">Signed in as <span className="text-[#4B5563] font-medium">{user?.email}</span>.</p>
      {error && <div role="alert" className="text-[12px] text-[#9B1F1F] bg-[#FBE9E9] border border-[#F1D2D2] rounded-md px-3 py-2">{error}</div>}
      <div className="space-y-2">
        <Button variant="primary" className="w-full" icon={busy === 'accept' ? Loader2 : undefined} disabled={!!busy} onClick={accept}>Accept invitation</Button>
        <Button variant="ghost" className="w-full" icon={busy === 'decline' ? Loader2 : undefined} disabled={!!busy} onClick={decline}>Decline</Button>
      </div>
    </div>
  );
};

const Frame = ({ children }) => (
  <div className="min-h-screen w-full flex items-center justify-center bg-[#FBFAF7] px-4 py-8">
    <div className="w-full max-w-[400px] bg-white border border-[#E6E4E1] rounded-[10px] px-6 py-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">{children}</div>
  </div>
);

const Problem = ({ title, body, action }) => (
  <div className="space-y-3">
    <span className="w-9 h-9 rounded-full bg-[#FEF3C7] text-[#8A5A12] inline-flex items-center justify-center"><MailWarning className="w-4 h-4" /></span>
    <h1 className="text-[16px] font-semibold text-[#1F2937]">{title}</h1>
    <p className="text-[12.5px] text-[#6B7280]">{body}</p>
    {action}
  </div>
);

/** The organizer dashboard on this console's own domain (dashboard.<domain>), or null locally. */
const dashboardUrl = () => derivedHostUrl('dashboard', null);
